import { DISALLOWED_PROPS, errorStatesTextType, hasChildren } from '../../../utils/copy';
import Case from 'case';
import * as KitchenData from '@inmoment/the-kitchen-data';
import { documentation, getComponentProps } from '../../../utils/Documentation';
import { hasName } from '@inmoment/the-kitchen/props/the-kitchen';
import { ErrorObject } from '@inmoment/the-kitchen';

const JSONProps = ['buttons', 'maxCharsAllowed'];

export function formatPropObjectForReact<T extends Record<string, any>>(propObj: T, name: string) {
  let propString = `<${name} \n\t`;
  propString += getRequiredProps(name)
    .filter((name) => !propObj.hasOwnProperty(name))
    .map((name) => `${name}={${name}}\n\t`)
    .join('');

  for (const key in propObj) {
    if (propObj[key] !== undefined && willBeInlined(key)) {
      // each if statement represents a type of data
      // booleans or numbers
      if (typeof propObj[key] === 'boolean' || typeof propObj[key] === 'number') {
        propString += `${key}={${propObj[key]}}\n\t`;
      }

      // data
      else if (key === 'data') {
        propString += `${key}={data}\n\t`;
      }

      // status
      else if (key === 'status') {
        propString += `${key}=${formatStatusProp(name, key, propObj[key], 'react')}\n\t`;
      }

      // object
      else if (JSONProps.includes(key)) {
        propString += `${key}={${JSON.stringify(propObj[key])}}\n\t`;
      }

      // string
      else {
        propString += `${key}="${propObj[key]}"\n\t`;
      }
    }
  }

  if (hasChildren.includes(name)) {
    propString += '>\n\t';
    propString += KitchenData[`_data${name}`].childrenString;
    propString += `\n</${name}>\n`;
  } else {
    propString += '\n/>';
  }
  return propString;
}

export function formatPropObjectForWeb<T extends Record<string, any>>(propObj: T, name: string) {
  const webName = pascalToKebab(name);
  let inlineString = `<${webName}`,
    scriptString = `<script>
  const component = document.querySelector('${name}');
  `;

  for (const key in propObj) {
    if (willBeInlined(key)) {
      if (propObj[key] !== undefined) {
        if (propObj[key] === true || propObj[key] === false || typeof propObj[key] === 'number') {
          if (propObj[key] !== 0) {
            scriptString += `  component.${key} = ${propObj[key]};\n`;
          }
        } else if (key === 'data') {
          scriptString += `  component.data = data;\n`;
        } else if (key === 'status') {
          scriptString += `\tcomponent.${key}=${formatStatusProp(name, key, propObj[key], 'web')};\n\t`;
        } else if (JSONProps.includes(key)) {
          scriptString += `  const ${key} = ${JSON.stringify(propObj[key])};\n`;
          scriptString += `  component.${key} = ${key};`;
        } else {
          inlineString += ` ${key}="${propObj[key]}"`;
        }
      }
    }
  }

  inlineString += '>';
  if (hasChildren.includes(name)) {
    inlineString += '\n\t';
    inlineString += KitchenData[`_data${name}`].angularString;
    inlineString += '\n';
  }
  inlineString += `</${webName}>\n\n\n`;

  scriptString += `\n\n</script>\n`;
  return inlineString + scriptString;
}

export function formatPropObjectForAngular<T extends Record<string, any>>(propObj: T, name: string) {
  const webName = pascalToKebab(name);
  let angularString = `<${webName}\n`;

  for (const key in propObj) {
    if (willBeInlined(key)) {
      if (propObj[key] !== undefined) {
        if (propObj[key] === true || propObj[key] === false || typeof propObj[key] === 'number') {
          if (propObj[key] !== 0) {
            angularString += ` [${key}]="${propObj[key]}"\n`;
          }
        } else if (key === 'data') {
          angularString += `  [data]="data"`;
        } else if (key === 'status') {
          angularString += `[${key}]=${formatStatusProp(name, key, propObj[key], 'angular')}\n`;
        } else if (JSONProps.includes(key)) {
          angularString += `[${key}]="${JSON.stringify(propObj[key]).replace(/"/g, "'")}"\n`;
        } else {
          angularString += ` ${key}="${propObj[key]}"\n`;
        }
      }
    }
  }
  angularString += '>';

  if (hasChildren.includes(name)) {
    angularString += '\n\t';
    angularString += KitchenData[`_data${name}`].angularString;
    angularString += '\n';
  }
  angularString += `</${webName}>\n\n\n`;

  return angularString;
}

function getRequiredProps(componentName: string) {
  if (!(componentName in documentation.byComponent)) {
    console.error(`Cannot get required props from unknown component "${componentName}"`);
    return [];
  }
  const component = documentation.byComponent[componentName];
  return getComponentProps(component)
    .filter(hasName)
    .filter((prop) => prop.required)
    .map((prop) => prop.name);
}

function formatStatusProp(componentName: string, propName: string, value: ErrorObject, framework: 'react' | 'web' | 'angular') {
  const component = documentation.byComponent[componentName];
  const propType = getComponentProps(component).find((prop) => prop.name === propName)?.type;

  if (propType === errorStatesTextType) {
    return `"${value}"`;
  }

  const statusStr = `{
  state: "${value.state}",
  ${value.text ? `text: "${value.text}",` : ''}
}`;

  if (framework === 'react') {
    return `{${statusStr}}`;
  } else if (framework === 'web') {
    return statusStr;
  } else if (framework === 'angular') {
    return `"${statusStr}"`;
  }
}

function willBeInlined(key: string) {
  return !DISALLOWED_PROPS.includes(key) || key === 'data' || key === 'isVisible' || key === 'value';
}

export function pascalToKebab(componentName: string) {
  return `xi-${Case.kebab(componentName.replace(/^XI/, ''))}`;
}
